import { FulfillmentDocumentBase } from "@/services/backend/relationships/types/fulfillment-document-base";
import { CommitmentDocumentBase } from "@/services/backend/relationships/types/commitment-document-base";
import { Reference } from "@/services/backend/documents/types/reference-number";
import { DocumentStateBase } from "@/services/backend/documents/types/state";
import { UUID } from "@bitory-gmbh/nidavellir";
import { Document, Artefact } from "@bitory-gmbh/bsm";
import { Address } from "@/services/backend/addresses/types/address";
import { Contact } from "@/services/backend/contacts/types/contact";
import { Representative } from "@/services/backend/representatives/types/representative";
import { StructuralAssessment } from "@/services/backend/htz/inspection/types/structural-assessment";
import { VisualAssessment } from "@/services/backend/htz/inspection/types/visual-assessment";
import { MicrobialAssessment } from "@/services/backend/htz/inspection/types/microbial-assessment";
import { Photo } from "@/services/backend/htz/inspection/types/photo";
import { Item } from "@/services/backend/htz/position-catalog/types/position-catalog";
import { EntityId } from "@/shared/types/entity-id";
import { joinFieldName } from "@/shared/lib/errors/err-validation-failed-context";
import type { PositionPatch } from "@/services/backend/htz/work-order/api/work-order-api";
import {
  containsSampleId,
  Delivery,
} from "@/services/backend/samples/types/delivery";
import { Common } from "@/services/backend/documents/types/common";
import { State } from "@/services/backend/htz/work-order/components/work-order-state";

export const HTZ_WORK_ORDER_NAMESPACE = "core.htz.domain.workorder.WorkOrder";

export interface WorkOrder
  extends
    Document,
    Artefact,
    DocumentStateBase,
    Common,
    Reference,
    CommitmentDocumentBase,
    FulfillmentDocumentBase {
  workOrderState: State;
  executingBranchId: string;
  beginDate?: string;
  endDate?: string;
  teamLeader: string;
  internalComment: string;
  address: Address;
  contact: Contact;
  representative: Representative;
}

export function assessmentsWithoutDelivery(
  sampleDeliveries: Delivery[],
  positions: Position[],
): MicrobialAssessment[] {
  return positions
    .map((pos) => pos.microbialAssessments ?? [])
    .flat()
    .filter((a) =>
      sampleDeliveries.every((delivery) => {
        if (delivery.cancelled) {
          return true;
        }
        return !containsSampleId(delivery, String(a.sampleId));
      }),
    );
}

export interface Position {
  id: string;
  workOrderId: string;
  companyId: UUID;
  branchId: UUID;
  number: number;
  completed: boolean;
  ahuId: EntityId;
  ahuDataId: EntityId;
  title: string;
  description: string;
  unitCount: number;
  unitPrice: number;
  totalPrice: number;
  unitHours: number;
  totalHours: number;
  optional: boolean;
  structuralInspectionRequired: boolean;
  structuralAssessments: StructuralAssessment[];
  visualInspectionRequired: boolean;
  visualAssessments: VisualAssessment[];
  microbialInspectionRequired: boolean;
  microbialAssessments: MicrobialAssessment[];
  photoDocumentationRequired: boolean;
  photos: Photo[];
}

/**
 * positionPatchFromCatalog returns the fields a position created from the
 * catalog item is prefilled with.
 */
export function positionPatchFromCatalog(item: Item): PositionPatch {
  return {
    title: item.copyTitle ? item.title : "",
    description: item.copyDescription ? item.description : "",
    unitPrice: item.copyUnitPrice ? item.unitPrice : 0,
    unitHours: item.copyUnitHours ? item.unitHours : 0,
    structuralInspectionRequired: item.structuralInspectionRequired,
    visualInspectionRequired: item.visualInspectionRequired,
    microbialInspectionRequired: item.microbiologicalInspectionRequired,
    photoDocumentationRequired: item.photoDocumentationRequired,
  };
}

/**
 * positionFieldName returns the key of a field of the position in the
 * ErrValidationFailedContext of the work order, e.g. "positions.<id>.title"
 * or "positions.<id>.photos.<photoId>.comment".
 */
export function positionFieldName(
  position: Pick<Position, "id">,
  ...fields: string[]
): string {
  return joinFieldName("positions", position.id, ...fields);
}

export function updateUnitCount(
  position: Position,
  unitCount: number,
): Position {
  return {
    ...position,
    unitCount,
    totalPrice: unitCount * position.unitPrice,
    totalHours: unitCount * position.unitHours,
  };
}

export function updateUnitPrice(
  position: Position,
  unitPrice: number,
): Position {
  return {
    ...position,
    unitPrice,
    totalPrice: unitPrice * position.unitCount,
  };
}

export function updateUnitHours(
  position: Position,
  unitHours: number,
): Position {
  return {
    ...position,
    unitHours,
    totalHours: unitHours * position.unitCount,
  };
}

/* helpers for optimistic updates of a position. No validation on purpose,
 * the backend validates. */
export function patchPositionStructuralAssessment(
  position: Position,
  assessment: StructuralAssessment,
): Position {
  return {
    ...position,
    structuralAssessments: position.structuralAssessments.map((a) =>
      a.id === assessment.id ? assessment : a,
    ),
  };
}

export function patchPositionVisualAssessment(
  position: Position,
  assessment: VisualAssessment,
): Position {
  return {
    ...position,
    visualAssessments: position.visualAssessments.map((a) =>
      a.id === assessment.id ? assessment : a,
    ),
  };
}

export function patchPositionPhoto(position: Position, photo: Photo): Position {
  return {
    ...position,
    photos: position.photos.map((p) => (p.id === photo.id ? photo : p)),
  };
}
